// src/tradeEngine.js
// Build 1 (Phase 1): live scoring path extracted verbatim from App.js — behavior-identical.
// Contains the canonical inline math (rsi, calculateIndicators incl. ema/macdCalc/atr),
// getConfirmationTimeframes, and scoreTrade (the momentumScore + exit path, App.js L802–931).
// Nothing changed: breakout slice(0,10)/slice(0,40), the ===10 exit quirk, and all tag/mode/
// threshold literals are preserved exactly. Fixes/consolidation are later builds.

import { CONFIG } from "./config";
import { findFairValueGaps, getSupportResistance } from "./levels";
export { getSupportResistance, isZoneUnconsumed } from "./levels";

// Wilder’s RSI (smoothed) to match TradingView default
export const rsi = (values, period = 14) => {
  if (!values || values.length <= period) return 50;
  // Calculate initial average gain/loss
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const change = values[i] - values[i - 1];
    if (change >= 0) gainSum += change;
    else lossSum += -change;
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  // Wilder smoothing over the rest of the series
  for (let i = period + 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
  }
  const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
};

export const calculateIndicators = (candles) => {
  if (!candles || candles.length === 0) return {};

  // Support both formatted objects and raw Coinbase arrays
  const isArrayForm = Array.isArray(candles[0]);
  const getClose = (c) => (isArrayForm ? (c[4] ?? c.close) : c.close);
  const getHigh  = (c) => (isArrayForm ? (c[2] ?? c.high)  : c.high);
  // FIX (was c[3], the OPEN column — misread as "low" for every ATR/True-Range calc since this
  // function existed). Positional candle shape is [time, low, high, open, close, volume], so the
  // correct low index is c[1]. Confirmed via full-codebase recon that atrValue (the only output
  // this feeds) has exactly one real consumer system-wide: worker.cjs's labelOne() outcome
  // labeler (win/loss/expired grading of already-fired signals). scoreTrade() never destructures
  // atrValue — confirmed in both src/tradeEngine.js and the bundled engine.bundle.cjs — so this
  // does NOT change confidence scoring, entry/exit/target prices, or fire/no-fire decisions for
  // any live or future signal. It only corrects the ATR used to grade past signals' outcomes.
  const getLow   = (c) => (isArrayForm ? (c[1] ?? c.low)   : c.low);

  let closes = candles.map(getClose);
  let highs  = candles.map(getHigh);
  let lows   = candles.map(getLow);

  // Ensure arrays are in oldest -> newest order.
  // Coinbase array form is newest-first; formatted objects may also be newest-first if time descends.
  const needsReverse = isArrayForm || (candles[0]?.time && candles[1]?.time && candles[0].time > candles[1].time);
  if (needsReverse) {
    closes = closes.slice().reverse();
    highs  = highs.slice().reverse();
    lows   = lows.slice().reverse();
  }

  const ema = (arr, period) => {
    if (!arr.length) return [];
    const k = 2 / (period + 1);
    return arr.reduce((acc, val, i) => {
      if (i === 0) return [val];
      acc.push(val * k + acc[i - 1] * (1 - k));
      return acc;
    }, []);
  };

  const macdCalc = (data, short = 12, long = 26, signal = 9) => {
    const emaShort = ema(data, short);
    const emaLong  = ema(data, long);
    const macdLine = emaShort.map((v, i) => v - emaLong[i]);
    const start    = Math.max(0, long - short);
    const signalLn = ema(macdLine.slice(start), signal);
    return {
      macd: macdLine[macdLine.length - 1],
      signal: signalLn[signalLn.length - 1]
    };
  };

  // Wilder RSI uses the helper defined above and expects oldest->newest closes
  const rsiValue = rsi(closes);
  const ema9 = ema(closes, 9).pop();
  const { macd, signal } = macdCalc(closes);

  // ATR (14) based on highs/lows/closes
  const atr = (period = 14) => {
    if (closes.length < 2) return 0;
    const trs = [];
    for (let i = 1; i < closes.length; i++) {
      const prevClose = closes[i - 1];
      const tr = Math.max(
        highs[i] - lows[i],
        Math.abs(highs[i] - prevClose),
        Math.abs(lows[i] - prevClose)
      );
      trs.push(tr);
    }
    const recent = trs.slice(-period);
    return recent.reduce((a, b) => a + b, 0) / (recent.length || 1);
  };

  const atrValue = atr(14);
  return { ema9, rsiValue, macd, signal, atrValue };
};

// Helper function to return the confirmation timeframes for a given label
export function getConfirmationTimeframes(label) {
    // Define buckets:
    // Short-Term Bucket: 5m, 15m, 30m, 1h, 3hr
    // Mid-Term Bucket: 4h, 5hr, 8hr, 10hr
    // Day Trade Bucket: 12hr, 15hr, 18hr, 20hr, 1d

    const confirmationMap = {
      "1m": ["5m"],                 // 1m signal confirmed by 5m
     "5m": ["15m", "1h"],           // 5m signal confirmed by 15m and 1h
     "15m": ["1h", "6h"],           // 15m signal confirmed by 1h and 6h
     "1h": ["6h"],                // 1h signal confirmed by 6h
     "6h": [],                    // 6h stands alone
     "1d": []                     // 1d stands alone
   };


    return confirmationMap[label] || [];
  }

// ── REGIME CLASSIFIER v1 (Variant 2) — NEW, OBSERVE-ONLY. None of the three functions below
//    touch scoreTrade's scoring math, calculateIndicators, or any fire gate. They derive a
//    deterministic per-asset/per-timeframe regime label that scoreTrade attaches to its return
//    bundle as `regime` for the regime_states write path only.

// detectStructure — minimal swing-point structure read over oldest→newest object candles.
// Pivot high = c.high STRICTLY greater than the high of N candles on EACH side; pivot low =
// c.low STRICTLY less than the low of N candles on each side. N=2 → 5-candle window. Lows are
// read from c.low ONLY (never calculateIndicators' getLow c[3] open-as-low bug). From the last
// two CONFIRMED pivot highs + last two pivot lows: HH+HL → "up", LH+LL → "down", else "none"
// (mixed or insufficient pivots).
export const detectStructure = (orderedCandles, N = 2) => {
  if (!orderedCandles || orderedCandles.length < 2 * N + 1) return "none";
  const pivotHighs = [];
  const pivotLows = [];
  for (let i = N; i < orderedCandles.length - N; i++) {
    const h = orderedCandles[i].high;
    const l = orderedCandles[i].low;
    let isHigh = true;
    let isLow = true;
    for (let j = i - N; j <= i + N; j++) {
      if (j === i) continue;
      if (orderedCandles[j].high >= h) isHigh = false; // not a strict local max
      if (orderedCandles[j].low <= l) isLow = false;    // not a strict local min
    }
    if (isHigh) pivotHighs.push(h);
    if (isLow) pivotLows.push(l);
  }
  if (pivotHighs.length < 2 || pivotLows.length < 2) return "none";
  const lastHigh = pivotHighs[pivotHighs.length - 1];
  const prevHigh = pivotHighs[pivotHighs.length - 2];
  const lastLow = pivotLows[pivotLows.length - 1];
  const prevLow = pivotLows[pivotLows.length - 2];
  if (lastHigh > prevHigh && lastLow > prevLow) return "up";   // HH + HL
  if (lastHigh < prevHigh && lastLow < prevLow) return "down"; // LH + LL
  return "none";
};

// classifyRegime (Variant 2) — weighted sub-scores → signed direction + conviction magnitude →
// label band. All inputs are already in hand at the scoreTrade call site (structure/macdSlope/
// rsiBand are derived there from existing values). Pure; returns the full sub-score breakdown so
// the write path is fully observable. Does NOT feed confidenceScore or any gate.
export const classifyRegime = ({ structure, macdSlope, rsiBand, trendUp, volumeSurge }) => {
  // directionScore — signed.
  let directionScore = 0;
  if (structure === "up") directionScore += 2;
  else if (structure === "down") directionScore -= 2;
  if (macdSlope > 0) directionScore += 1;
  else if (macdSlope < 0) directionScore -= 1;
  directionScore += rsiBand;              // +1 / -1 / 0
  directionScore += trendUp ? 1 : -1;     // existing emaSlope>0 flag
  // convictionScore — magnitude 0..4.
  let convictionScore = 0;
  if (structure !== "none") convictionScore += 1;
  if (volumeSurge) convictionScore += 1;
  if (Math.abs(macdSlope) > 0) convictionScore += 1;
  if (rsiBand !== 0) convictionScore += 1;
  // label bands.
  let label;
  if (convictionScore <= 1) label = "choppy";
  else if (directionScore >= 2 && convictionScore >= 2) label = "trending_up";
  else if (directionScore <= -2 && convictionScore >= 2) label = "trending_down";
  else label = "transitioning";
  return { label, directionScore, convictionScore, structure, macdSlope, rsiBand };
};

// scoreTrade — the live suggestion scorer, lifted verbatim from App.js fetchIndicators (L802–931).
// Inputs are the values fetchIndicators computes before the scoring block; the return bundle holds
// what the grading blocks and the coin-update display read afterward.
export function scoreTrade({
  macd,
  signal,
  rsiValue,
  macdHistogramHistory,
  coinPrice,
  closePrices,
  latestVolume,
  volumeAvg,
  emaSlope,
  localResistances,
  candles,
  label,
  timeframeCandlesMap
}) {
  // ── LAYER 1 (Phase 6) — REGIME GATE READ. Computed here at the top of scoreTrade so it
  //    sits at the seam a downgrade would also attach to, but it does NOT touch the score
  //    below: regimeAligned is returned and the HARD gate is applied in fireGate (one-line
  //    swap to soft later). Long-only regime = EMA50 rising + price above EMA50 + ADX trending.
  //    All tunables are PROVISIONAL, named for by-eye tuning in verification.
  const REGIME_EMA_PERIOD = 50;        // EMA period for the trend filter
  const REGIME_SLOPE_LOOKBACK = 4;     // candles back to measure EMA50 slope
  const REGIME_SLOPE_MIN_PCT = 0.05;   // % rise over the lookback to count as "rising" (not flat)
  const REGIME_ADX_PERIOD = 14;        // standard Wilder ADX period
  const REGIME_ADX_MIN = 25;           // ADX >= this = trending (provisional)
  // closePrices arrives OLDEST→NEWEST (prep.cjs reverses the newest-first candles); EMA runs
  // oldest→newest, so consume closePrices directly.
  const regimeEma = (arr, period) => {
    const k = 2 / (period + 1);
    let prev;
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      prev = i === 0 ? arr[0] : arr[i] * k + prev * (1 - k);
      out.push(prev);
    }
    return out;
  };
  const ema50Series = regimeEma(closePrices, REGIME_EMA_PERIOD);
  const ema50Latest = ema50Series[ema50Series.length - 1];
  const ema50Prior = ema50Series[ema50Series.length - 1 - REGIME_SLOPE_LOOKBACK];
  const ema50SlopePct = ema50Prior > 0 ? ((ema50Latest - ema50Prior) / ema50Prior) * 100 : 0;
  const ema50Rising = ema50SlopePct >= REGIME_SLOPE_MIN_PCT;
  const priceAboveEma50 = coinPrice > ema50Latest;
  // ADX (Wilder, 14). Source H/L/C from the RAW candle indices high=c[2], low=c[1], close=c[4]
  // to AVOID calculateIndicators' getLow=c[3] bug (reads OPEN-as-low; its repair is a later
  // commit). `candles` are newest-first positional arrays → reverse to oldest→newest first.
  const regimeCandles = candles.slice().reverse();
  const regimeHigh = regimeCandles.map((c) => c[2]);
  const regimeLow = regimeCandles.map((c) => c[1]);
  const regimeClose = regimeCandles.map((c) => c[4]);
  const computeAdx = (high, low, close, period) => {
    const len = high.length;
    if (len < period * 2) return 0;   // need ~2×period for a stable Wilder ADX; else fail-closed (no fire)
    const trs = [], plusDM = [], minusDM = [];
    for (let i = 1; i < len; i++) {
      const upMove = high[i] - high[i - 1];
      const downMove = low[i - 1] - low[i];
      plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
      minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
      trs.push(Math.max(
        high[i] - low[i],
        Math.abs(high[i] - close[i - 1]),
        Math.abs(low[i] - close[i - 1])
      ));
    }
    // Wilder smoothing: seed = sum of first `period`, then sm = sm - sm/period + current.
    const wilder = (arr) => {
      let sm = 0;
      for (let i = 0; i < period; i++) sm += arr[i];
      const out = [sm];
      for (let i = period; i < arr.length; i++) {
        sm = sm - sm / period + arr[i];
        out.push(sm);
      }
      return out;
    };
    const trS = wilder(trs), plusS = wilder(plusDM), minusS = wilder(minusDM);
    const dx = [];
    for (let i = 0; i < trS.length; i++) {
      const plusDI = trS[i] === 0 ? 0 : (plusS[i] / trS[i]) * 100;
      const minusDI = trS[i] === 0 ? 0 : (minusS[i] / trS[i]) * 100;
      const diSum = plusDI + minusDI;
      dx.push(diSum === 0 ? 0 : (Math.abs(plusDI - minusDI) / diSum) * 100);
    }
    if (dx.length < period) {
      return dx.length ? dx.reduce((a, b) => a + b, 0) / dx.length : 0;
    }
    let adx = 0;
    for (let i = 0; i < period; i++) adx += dx[i];
    adx /= period;
    for (let i = period; i < dx.length; i++) adx = (adx * (period - 1) + dx[i]) / period;
    return adx;
  };
  const adxValue = computeAdx(regimeHigh, regimeLow, regimeClose, REGIME_ADX_PERIOD);
  const adxTrending = adxValue >= REGIME_ADX_MIN;
  // Fail-closed: insufficient history → EMA/ADX degrade to non-aligned (no fire), the safe
  // direction for a hard gate.
  const regimeAligned = ema50Rising && priceAboveEma50 && adxTrending;

  let momentumScore = 0;
  if (macd > signal) momentumScore += 1;
  if (macdHistogramHistory.length >= 3) {
    const histCurrent = macdHistogramHistory[macdHistogramHistory.length - 1];
    const histPrev1 = macdHistogramHistory[macdHistogramHistory.length - 2];
    const histPrev2 = macdHistogramHistory[macdHistogramHistory.length - 3];
    const momentumGrowing = histCurrent > histPrev1 && histPrev1 > histPrev2;
    if (momentumGrowing) {
      momentumScore += 2;
    }
  }
  const shortBreakout = coinPrice > Math.max(...closePrices.slice(-10));
  const longBreakout = coinPrice > Math.max(...closePrices.slice(-40));
  if (shortBreakout && longBreakout) momentumScore += 2;
  else if (shortBreakout) momentumScore += 1;
  const volumeSurge = latestVolume > volumeAvg;
  if (volumeSurge) momentumScore += 2;
  const trendUp = emaSlope > 0;
  if (trendUp) momentumScore += 1;
  // 📈 Require RSI to be rising to award momentum point
  const rsiHistory = closePrices.slice(-17).map((_, i, arr) => {
    if (i < 14) return null;
    const rsiVal = rsi(arr.slice(i - 14, i + 1));
    return rsiVal;
  }).filter(Boolean);
  if (rsiHistory.length >= 3) {
    const recentRSIs = rsiHistory.slice(-3);
    const rsiIsRising = recentRSIs[2] > recentRSIs[1] && recentRSIs[1] > recentRSIs[0];
    if (rsiIsRising) {
      momentumScore += 1;
    }
    // New RSI scoring logic:
    if (rsiValue >= CONFIG.rsi.zones.low.min && rsiValue <= CONFIG.rsi.zones.low.max) {
      momentumScore += 1;
    } else if (rsiValue >= CONFIG.rsi.zones.mid.min && rsiValue <= CONFIG.rsi.zones.mid.max) {
      momentumScore += 2;
    } else if (rsiValue >= CONFIG.rsi.zones.high.min && rsiValue <= CONFIG.rsi.zones.high.max) {
      momentumScore += 1;
    }
  }
  if (rsiValue > CONFIG.rsi.overbought) momentumScore -= 1;

  // --- Stock RSI (SRSI) scoring ---
  // Calculate Stochastic RSI K
  let stochRsiK = null;
  let stochRsiHistory = [];
  if (closePrices.length >= 15) {
    // Compute RSI history for SRSI
    const rsiArr = [];
    for (let i = 0; i < closePrices.length - 13; i++) {
      const rsiSlice = closePrices.slice(i, i + 14 + 1);
      rsiArr.push(rsi(rsiSlice));
    }
    // StochRSI calculation
    for (let i = 13; i < rsiArr.length; i++) {
      const rsiWindow = rsiArr.slice(i - 13, i + 1);
      const minRsi = Math.min(...rsiWindow);
      const maxRsi = Math.max(...rsiWindow);
      const currentRsi = rsiArr[i];
      let k = 0;
      if (maxRsi !== minRsi) {
        k = ((currentRsi - minRsi) / (maxRsi - minRsi)) * 100;
      } else {
        k = 0;
      }
      stochRsiHistory.push(k);
    }
    stochRsiK = stochRsiHistory[stochRsiHistory.length - 1];
  }

  // --- Confirmation from higher timeframes using fetched data ---
  let confirmFromHigherTimeframes = false;
  const confirmationTFs = getConfirmationTimeframes(label);
  for (const tf of confirmationTFs) {
    const htfCandles = timeframeCandlesMap[tf];
    if (!htfCandles) continue;
    const { macd: htfMacd, signal: htfSignal, rsiValue: htfRsi, ema9: htfEma9 } = calculateIndicators(htfCandles);
    const htfPriceOneCandleAgo = htfCandles[1]?.[4] || 0;
    const macdBullish = htfMacd > htfSignal;
    const rsiHealthy = htfRsi > 54 && htfRsi < 70;
    const emaRising = htfEma9 > htfPriceOneCandleAgo;
    if (macdBullish && rsiHealthy && emaRising) {
      confirmFromHigherTimeframes = true;
      break;
    }
  }
  if (confirmFromHigherTimeframes) momentumScore += 2;
  if (volumeAvg < 100 && emaSlope < 0.01) momentumScore -= 1;
  const body = Math.abs(candles[0][4] - candles[0][1]);
  const wick = candles[0][2] - candles[0][3];
  const wickRatio = wick / (body || 1);
  if (wickRatio > 3) momentumScore -= 1;
  const confidenceScore = momentumScore;
  // FVG detection (display-only; does not affect confidenceScore).
  // Engine holds candles newest-first as positional Coinbase arrays
  // [time, low, high, open, close, volume]; findFairValueGaps needs
  // oldest->newest object candles. Convert on a copy at this one call site.
  const orderedCandles = candles.slice().reverse().map((c) => ({
    time: c[0], low: c[1], high: c[2], open: c[3], close: c[4], volume: c[5],
  }));
  const fvgs = findFairValueGaps(orderedCandles);
  // S/R detection (display-only; does not affect confidenceScore). Reuses the
  // same oldest->newest object copy built above for FVG.
  const supportResistance = getSupportResistance(orderedCandles);
  // ── LAYER 2 (Phase 6) — STRUCTURE-BREAK GATE READ. Reuses the supportResistance result
  //    above (no second getSupportResistance call, no swing recompute). TRUE when price has
  //    cleared a real, high-strength level that now reads as support just beneath it — i.e.
  //    a level price RECENTLY broke through, not ancient support far below. Returned and
  //    hard-gated in fireGate, mirroring regimeAligned. Does NOT touch the score.
  //    Provisional tunables, named here for by-eye tuning.
  const STRUCTURE_STRENGTH_MIN = 50;     // min scoreLevel strength to count as "real"
  const STRUCTURE_PROXIMITY_PCT = 2;     // cleared level must be within 2% below price
  const RETEST_KILL_BUFFER_PCT = 0.25;   // L3: close below priceHigh by >this % = killed
  const RETEST_STALE_WINDOW = 8;         // L3: candles after break to await a held retest
  const BREAK_BODY_MULT = 1.5;           // L2 qualifier: break body >= this × median body (PROVISIONAL — unearned, tune Build 5)
  const BREAK_VOL_MULT = 2.0;            // L2 qualifier: break volume >= this × median volume (PROVISIONAL — unearned, tune Build 5)
  const BREAK_RECENCY_N = 10;            // L2 qualifier: up-cross must be within last N closed candles (PROVISIONAL — unearned, tune Build 5)
  const BREAK_MEDIAN_N = 10;             // L2 qualifier: median lookback in closed candles (PROVISIONAL — unearned, tune Build 5)
  // Local median (independent copy — NOT imported/hoisted from levels.js/bounce/breakout). Sorted
  // middle; mean of the two middles when even.
  const median = (arr) => {
    if (!arr.length) return 0;
    const s = arr.slice().sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  };
  // Break-qualifier reference: median body & median volume over the last BREAK_MEDIAN_N closed
  // candles (orderedCandles is object-form oldest→newest). Fewer than N candles OR a zero median →
  // the qualifier FAILS CLOSED (no qualified break can exist this pass → structureBreak false).
  const breakMedWindow = orderedCandles.slice(-BREAK_MEDIAN_N);
  const medianBody =
    breakMedWindow.length >= BREAK_MEDIAN_N
      ? median(breakMedWindow.map((c) => Math.abs(c.close - c.open)))
      : 0;
  const medianVol =
    breakMedWindow.length >= BREAK_MEDIAN_N
      ? median(breakMedWindow.map((c) => c.volume || 0))
      : 0;
  const breakQualifierReady = medianBody > 0 && medianVol > 0;
  // qualifiedBreakIdx — the MOST RECENT up-cross of `level` within the last BREAK_RECENCY_N candles
  // that ALSO shows expansion (body >= BREAK_BODY_MULT × medianBody AND volume >= BREAK_VOL_MULT ×
  // medianVol). Returns the break-candle index, or -1 when none qualifies (qualifier not ready, no
  // up-cross in the recency window, or the most-recent up-cross fails expansion). Pure read.
  const qualifiedBreakIdx = (level) => {
    if (!breakQualifierReady) return -1;
    const oldest = Math.max(1, orderedCandles.length - BREAK_RECENCY_N);
    for (let i = orderedCandles.length - 1; i >= oldest; i--) {
      const c = orderedCandles[i];
      const prev = orderedCandles[i - 1];
      if (prev.close <= level && c.close > level) {   // most recent up-cross in the recency window
        const body = Math.abs(c.close - c.open);
        const qualified =
          body >= BREAK_BODY_MULT * medianBody &&
          (c.volume || 0) >= BREAK_VOL_MULT * medianVol;
        return qualified ? i : -1;                     // the most-recent up-cross must itself qualify
      }
    }
    return -1;
  };
  // L2 — TRUE iff at least one strong, sub-proximity zone has a QUALIFIED break candle (expansion
  // in body AND volume within the recency window), not merely a strong level sitting below price.
  const structureBreak = supportResistance.support.some((z) =>
    z.strength >= STRUCTURE_STRENGTH_MIN &&
    z.priceHigh < coinPrice &&
    z.priceHigh >= coinPrice * (1 - STRUCTURE_PROXIMITY_PCT / 100) &&
    qualifiedBreakIdx(z.priceHigh) !== -1
  );
  // ── LAYER 3 (Phase 6) — RETEST-HOLD. Fires on the RETEST of a broken level, not the break
  //    candle: price broke a real level (L2), came BACK to it, and the old resistance HOLDS as
  //    support. Re-derives the broken zone(s) locally with L2's EXACT predicate as a .filter()
  //    (deliberate locked duplication — does NOT alter structureBreak above), INCLUDING the
  //    qualified-break requirement, and keys the retest walk off that SAME qualified break candle
  //    (not any up-cross). Walks the in-hand oldest->newest window forward from the break; no
  //    cross-pass state. Returned and hard-gated in fireGate, mirroring regimeAligned /
  //    structureBreak. Provisional knobs, by-eye tuning.
  const brokenZones = supportResistance.support.filter((z) =>
    z.strength >= STRUCTURE_STRENGTH_MIN &&
    z.priceHigh < coinPrice &&
    z.priceHigh >= coinPrice * (1 - STRUCTURE_PROXIMITY_PCT / 100) &&
    qualifiedBreakIdx(z.priceHigh) !== -1
  );
  let retestHeld = false;
  if (brokenZones.length > 0) {
    const zone = brokenZones[0];           // strongest broken zone (support is strength-desc sorted)
    const level = zone.priceHigh;
    const killLine = level * (1 - RETEST_KILL_BUFFER_PCT / 100);
    // Break candle = the QUALIFIED break candle for this zone (expansion up-cross), NOT any up-cross.
    const breakIdx = qualifiedBreakIdx(level);
    if (breakIdx !== -1) {
      // Walk forward up to RETEST_STALE_WINDOW candles after the break, watching for a return to
      // the level that HOLDS. No qualifying hold in the window → stays false (stale).
      const end = Math.min(orderedCandles.length - 1, breakIdx + RETEST_STALE_WINDOW);
      let returned = false;
      for (let i = breakIdx + 1; i <= end; i++) {
        const c = orderedCandles[i];
        if (c.low <= level) returned = true;                                  // reached back down to the level
        if (returned) {
          if (c.close < killLine) { retestHeld = false; break; }              // decisive close below = killed
          if (c.low <= level && c.close > level) { retestHeld = true; break; } // wick-tolerant close above = held
        }
      }
    }
  }
  // ── CONTEXT READS (momentum path) — two deterministic candle-body-vs-volume reads computed
  //    beside L2/L3, reading orderedCandles directly (object-form oldest→newest; newest element is
  //    the newest CLOSED candle — the forming candle was dropped upstream at fetch). Reuse the
  //    local `median` helper defined in the L2 block. Both are RETURNED and consumed by fireGate as
  //    a post-climb Watchlist CAP (downgrade, never drop) — they do NOT touch the score or any gate
  //    here. L5 emitter (candleRelations) is untouched — it stays a describer. Provisional pins.
  const ABSORB_VOL_MULT = 2.5;    // absorption volume >= this × window avg volume (PROVISIONAL — unearned, tune Build 5)
  const ABSORB_BODY_MULT = 0.6;   // absorption body <= this × window median body (PROVISIONAL — unearned, tune Build 5)
  const ABSORB_LOOKBACK_K = 5;    // closed candles scanned for an absorption candle (PROVISIONAL — unearned, tune Build 5)
  const DECAY_WINDOW = 3;         // closed candles for the effort-decay read (PROVISIONAL — unearned, tune Build 5)
  const DECAY_BODY_TOL = 0.8;     // each body >= this × previous body = holding/growing (PROVISIONAL — unearned, tune Build 5)
  // ABSORPTION — a heavy-volume, small-red candle stalling an up-move (a wall): red, volume ≥ mult ×
  // window avg, body ≤ mult × window median body, and net-rising into it (the candle immediately
  // before it in-window closed above the window's first close). Fails closed on a short window or a
  // zero avg/median. The candidate at ctxWindow[0] has no in-window predecessor → cannot qualify.
  const ctxWindow = orderedCandles.slice(-ABSORB_LOOKBACK_K);
  let absorptionDetected = false;
  if (ctxWindow.length >= ABSORB_LOOKBACK_K) {
    const winAvgVol = ctxWindow.reduce((s, c) => s + (c.volume || 0), 0) / ctxWindow.length;
    const winMedBody = median(ctxWindow.map((c) => Math.abs(c.close - c.open)));
    if (winAvgVol > 0 && winMedBody > 0) {
      absorptionDetected = ctxWindow.some((c, i) =>
        i > 0 &&                                                   // needs an in-window predecessor
        c.close < c.open &&                                        // red
        (c.volume || 0) >= ABSORB_VOL_MULT * winAvgVol &&          // heavy volume
        Math.abs(c.close - c.open) <= ABSORB_BODY_MULT * winMedBody && // small body
        ctxWindow[i - 1].close > ctxWindow[0].close               // net-rising into the wall
      );
    }
  }
  // EFFORT DECAY — over the last DECAY_WINDOW closed candles, bodies hold/grow while volume strictly
  // falls candle-over-candle (a move running out of fuel). Fewer than DECAY_WINDOW candles → false.
  let effortDecayDetected = false;
  const decayWindow = orderedCandles.slice(-DECAY_WINDOW);
  if (decayWindow.length >= DECAY_WINDOW) {
    effortDecayDetected = true;
    for (let i = 1; i < decayWindow.length; i++) {
      const a = decayWindow[i - 1];
      const b = decayWindow[i];
      const bodyA = Math.abs(a.close - a.open);
      const bodyB = Math.abs(b.close - b.open);
      if (!(bodyB >= DECAY_BODY_TOL * bodyA && (b.volume || 0) < (a.volume || 0))) {
        effortDecayDetected = false;
        break;
      }
    }
  }
  // ── WICK READS (context read #3, momentum path) — two LOCATION-AWARE reads on the newest CLOSED
  //    candle (orderedCandles[len-1]; object-form .low/.high/.open/.close — NEVER positional c[3]).
  //    DEFENSE HAMMER (tag-only): long lower wick defending the nearest unconsumed support BELOW.
  //    REJECTION WICK (caps grade at Watchlist in fireGate): long upper wick rejected at the nearest
  //    unconsumed resistance ABOVE. Zones reuse supportResistance.nearestSupportBelow /
  //    .nearestResistanceAbove (already unconsumed + nearest — no re-derivation). Reuse the local
  //    `median` helper. Fail closed: short window, zero median/avg, or no zone on that side → false.
  //    Both RETURNED and consumed by fireGate; they do NOT touch the score or any gate here.
  const WICK_BODY_MULT = 1.5;    // wick >= this × candle body (PROVISIONAL — unearned, tune Build 5)
  const WICK_RANGE_MULT = 0.6;   // wick >= this × medianRange (PROVISIONAL — unearned, tune Build 5)
  const WICK_VOL_MULT = 1.5;     // candle volume >= this × window avg volume (PROVISIONAL — unearned, tune Build 5)
  const WICK_LOC_SLACK = 0.5;    // location tolerance: within this × medianRange of the zone edge (PROVISIONAL — unearned, tune Build 5)
  const WICK_WINDOW_N = 10;      // medianRange & volume window in closed candles (PROVISIONAL — unearned, tune Build 5)
  let wickDefense = false;
  let wickRejection = false;
  let toppingWick = false;
  const wickWindow = orderedCandles.slice(-WICK_WINDOW_N);
  if (wickWindow.length >= WICK_WINDOW_N) {
    const wickMedianRange = median(wickWindow.map((c) => c.high - c.low));
    const wickAvgVol = wickWindow.reduce((s, c) => s + (c.volume || 0), 0) / wickWindow.length;
    const cur = orderedCandles[orderedCandles.length - 1];   // newest CLOSED candle
    const range = cur.high - cur.low;
    if (wickMedianRange > 0 && wickAvgVol > 0 && range > 0) {
      const body = Math.abs(cur.close - cur.open);
      const lowerWick = Math.min(cur.open, cur.close) - cur.low;
      const upperWick = cur.high - Math.max(cur.open, cur.close);
      const closePos = (cur.close - cur.low) / range;        // 0 = at low, 1 = at high
      const volOk = (cur.volume || 0) >= WICK_VOL_MULT * wickAvgVol;
      // DEFENSE HAMMER — long lower wick, close in top third, at/near nearest support below.
      const supZone = supportResistance.nearestSupportBelow;
      if (supZone) {
        const atSupport =
          cur.low <= supZone.priceHigh ||                             // low entered the zone
          cur.low - supZone.priceHigh <= WICK_LOC_SLACK * wickMedianRange; // or within slack above it
        wickDefense =
          lowerWick >= WICK_BODY_MULT * body &&
          lowerWick >= WICK_RANGE_MULT * wickMedianRange &&
          closePos >= 2 / 3 &&                                        // close in top third
          volOk &&
          atSupport;
      }
      // REJECTION WICK — mirror: long upper wick, close in bottom third, at/near nearest resistance above.
      const resZone = supportResistance.nearestResistanceAbove;
      if (resZone) {
        const atResistance =
          cur.high >= resZone.priceLow ||                             // high entered the zone
          resZone.priceLow - cur.high <= WICK_LOC_SLACK * wickMedianRange; // or within slack below it
        wickRejection =
          upperWick >= WICK_BODY_MULT * body &&
          upperWick >= WICK_RANGE_MULT * wickMedianRange &&
          closePos <= 1 / 3 &&                                        // close in bottom third
          volOk &&
          atResistance;
      }
      // ── TOPPING-WICK READ (context read #3b, momentum path) — LOCATION-FREE strength-with-
      //    pushback: a long upper wick on a GREEN (or flat) heavy-volume candle, NO zone required
      //    (the safety net for the SHIB case where nearestResistanceAbove was null and both the
      //    rejection-wick cap and overhead-room downgrade no-op'd). Reuses cur/body/upperWick/
      //    wickMedianRange/volOk already in scope. RETURNED and consumed by fireGate, which caps
      //    ONLY Strong+Room → Strong with it. Does NOT touch the score or any gate here.
      const TOPPING_WICK_BODY_MULT = 1.0;   // upper wick >= this × body (PROVISIONAL — unearned, tune Build 5)
      // reuses WICK_RANGE_MULT (0.6) and WICK_VOL_MULT (1.5) pins — same family
      toppingWick =
        cur.close >= cur.open &&                          // green or flat only
        upperWick >= TOPPING_WICK_BODY_MULT * body &&
        upperWick >= WICK_RANGE_MULT * wickMedianRange &&
        volOk;                                            // location-FREE: no zone required
    }
  }
  // ── FIRE-CANDLE QUALITY GATE (micro gate, momentum path) — OBJECTIVE drop, not a cap (fireGate
  //    kills the row when this fails, unlike the wick/context caps which only lower grade). The fire
  //    candle itself must show conviction: green or flat, a real body (>= half its own range), and
  //    at least one of body/volume expanding vs the prior candle. Blocks the small-body double-wick
  //    indecision candle that fired UNI 1d in chop. FRESH locals — the wick block's cur/body/range
  //    are block-scoped inside the guard above and out of scope here; declared anew rather than
  //    restructuring that block. Fail closed: fewer than 2 candles or range === 0 → false.
  const FIRE_BODY_RANGE_MULT = 0.5;  // body >= this × range (PROVISIONAL — unearned, tune Build 5)
  let fireCandleOk = false;
  if (orderedCandles.length >= 2) {
    const fcCur = orderedCandles[orderedCandles.length - 1];   // newest CLOSED candle
    const fcPrev = orderedCandles[orderedCandles.length - 2];
    const fcBody = Math.abs(fcCur.close - fcCur.open);
    const fcRange = fcCur.high - fcCur.low;
    const fcPrevBody = Math.abs(fcPrev.close - fcPrev.open);
    if (fcRange > 0) {
      fireCandleOk =
        fcCur.close >= fcCur.open &&                                      // green or flat
        fcBody >= FIRE_BODY_RANGE_MULT * fcRange &&                       // real body vs its range
        (fcBody >= fcPrevBody || (fcCur.volume || 0) >= (fcPrev.volume || 0)); // 1-of-2 vs prior
    }
  }
  // ── MACRO CHOP GATE (macro gate, momentum path) — a RANGE-POSITION read beside the MICRO
  //    fire-candle gate above. OBJECTIVE drop: detects the last CHOP_N closes clustered inside a
  //    range-relative band (range-bound chop), with a TREND-PULLBACK EXEMPTION that is itself KILLED
  //    when price sits right under the nearest overhead resistance. Fresh locals; reuses the in-scope
  //    `median` helper (Option A — median-range RECOMPUTED at the seam, deliberately NOT hoisted from
  //    the wick block's wickMedianRange) and supportResistance.nearestResistanceAbove. PASSES ON
  //    INSUFFICIENT DATA: unlike the micro gate's false default, a short window / zero median / a
  //    non-clustered window leaves chopGateOk TRUE — a deploy-safety choice so thin data never
  //    silently kills all momentum fires; ONLY a positively-detected chop sets it false.
  const CHOP_N = 5;                 // closes clustered over this many candles (PROVISIONAL — unearned, tune Build 5)
  const CHOP_BAND_MULT = 1.5;      // cluster band = this × medianRange (PROVISIONAL — unearned, tune Build 5)
  const CHOP_MEDIAN_N = 10;        // median-range lookback in closed candles (PROVISIONAL — unearned, tune Build 5)
  const TREND_LOOKBACK = 20;       // pullback exemption: travel measured this many candles back (PROVISIONAL — unearned, tune Build 5)
  const TREND_TRAVEL_MULT = 2.0;   // exemption requires travel >= this × medianRange (PROVISIONAL — unearned, tune Build 5)
  const CHOP_RES_PROX_MULT = 1.0;  // resistance within this × medianRange kills the exemption (PROVISIONAL — unearned, tune Build 5)
  let chopGateOk = true;
  if (orderedCandles.length >= TREND_LOOKBACK + 1) {
    const chopMedianRange = median(
      orderedCandles.slice(-CHOP_MEDIAN_N).map((c) => c.high - c.low)
    );
    if (chopMedianRange > 0) {
      const chopCloses = orderedCandles.slice(-CHOP_N).map((c) => c.close);
      const clustered =
        Math.max(...chopCloses) - Math.min(...chopCloses) <=
        CHOP_BAND_MULT * chopMedianRange;
      if (clustered) {
        const currentClose = orderedCandles[orderedCandles.length - 1].close;
        const resZone = supportResistance.nearestResistanceAbove;
        if (
          resZone != null &&
          resZone.priceLow - currentClose <= CHOP_RES_PROX_MULT * chopMedianRange
        ) {
          chopGateOk = false;                                  // clustered + wall overhead → chop, no exemption
        } else {
          const travel =
            currentClose -
            orderedCandles[orderedCandles.length - 1 - TREND_LOOKBACK].close;
          if (travel < TREND_TRAVEL_MULT * chopMedianRange) {
            chopGateOk = false;                                // clustered, no trend travel → range-bound chop
          }
          // else: travel clears the bar → pullback-in-trend → exemption holds → chopGateOk stays true
        }
      }
      // else: last CHOP_N closes NOT clustered → not chop → chopGateOk stays true
    }
  }
  // ── LAYER 4 (Phase 6) — STRUCTURE-DERIVED GEOMETRY. Replaces the pure-ATR exit target
  //    (ATR's low-leg is computed off open-as-low on the live array path — not trustworthy
  //    for distance, so ATR is cut from the target path). Real stop = nearest STRONG support
  //    below entry; real target = nearest STRONG resistance >= 2R above entry; realisticProfit
  //    is recomputed off that structure target (null when geometry is incomplete → no fire).
  //    Reuses the supportResistance zones already computed above (no rescan). Provisional
  //    tunables, named here for by-eye tuning; STRUCTURE_STRENGTH_MIN reused from the L2 block.
  // entryClose — the newest CLOSED candle's close (orderedCandles is oldest->newest; forming candle
  // dropped upstream). L4 geometry references THIS, not live spot (coinPrice), so the stop/2R floor/
  // proximity band/realisticProfit move only when a candle actually closes — not on every spot
  // wiggle. Zones already split on this same closed close (getSupportResistance currentPrice), so
  // both sides now share one stable reference and targets stop flipping scan-to-scan. L2/L3 stay on
  // spot this build (logged follow-up).
  const entryClose = orderedCandles[orderedCandles.length - 1].close;
  const ROOM_MIN_R = 2.0;            // target must be >= 2R above entry to fire
  const STOP_BUFFER_PCT = 0.3;       // stop sits this % below the support level it rests on
  const MAX_STOP_PCT = 5.0;   // reject setups whose stop sits more than this % below entry —
                              // a too-far stop inflates the 2R floor into an absurd target.
  const PROXIMITY_SKIP_FLOOR = 0.3;  // % above entry: below this a target is too close → SKIP.
                                     // Above it the 2R floor (minTargetPrice) governs, so Knob B
                                     // collapses to one skip threshold + the existing 2R floor.
                                     // PROXIMITY_KEEP_CEIL is intentionally NOT homed: with both
                                     // upper zones deferring to 2R it would carry no behavior, and
                                     // an unused pin trips eslint no-unused-vars (over baseline).
  // STOP: nearest STRONG support strictly below entry, buffered just under it.
  const stopCandidates = supportResistance.support
    .filter((z) => z.strength >= STRUCTURE_STRENGTH_MIN && z.priceHigh < entryClose)
    .sort((a, b) => b.priceHigh - a.priceHigh); // nearest below entry first
  const stopLevel = stopCandidates.length > 0 ? stopCandidates[0].priceHigh : null;
  const stopPrice = stopLevel !== null ? stopLevel * (1 - STOP_BUFFER_PCT / 100) : null;
  const rawRisk = stopPrice !== null ? entryClose - stopPrice : null;
  const riskDistance =
    rawRisk !== null && rawRisk / entryClose <= MAX_STOP_PCT / 100 ? rawRisk : null;
  // TARGET: nearest qualifying level above entry, chosen via the Knob B proximity skip.
  // minTargetPrice is the 2R floor (computation UNCHANGED); it is now consulted ONLY inside
  // passesProximityBand, not as a standalone filter clause.
  const minTargetPrice =
    riskDistance !== null ? entryClose + ROOM_MIN_R * riskDistance : null;
  // Knob B — proximity skip, measured vs ENTRY (entryClose). dist = (z.priceLow - entryClose) /
  // entryClose * 100. Above the skip floor the 2R floor (minTargetPrice) governs, so the band is
  // MONOTONIC — it only ever TIGHTENS the old filter, never selects below 2R:
  //   dist <  PROXIMITY_SKIP_FLOOR (0.3%)   → SKIP (too close to entry, regardless of 2R)
  //   dist >= PROXIMITY_SKIP_FLOOR          → KEEP iff it clears the 2R floor (priceLow >= minTargetPrice)
  // The original spec's two upper zones (middle / ≥0.7%) are now identical — both defer to 2R — so
  // the band collapses to one skip threshold + the existing 2R floor. That collapse IS the design
  // (skip too-close levels; 2R floors the rest); it is NOT re-split to preserve three zones for
  // their own sake. minTargetPrice (computation UNCHANGED) is referenced ONLY here, replacing the
  // old standalone `z.priceLow >= minTargetPrice` filter clause so it can't double-apply. Runs
  // AFTER the strength filter; the downstream [0] grab takes the nearest survivor.
  const passesProximityBand = (z) => {
    const dist = ((z.priceLow - entryClose) / entryClose) * 100;
    if (dist < PROXIMITY_SKIP_FLOOR) return false; // too close to entry → skip
    return z.priceLow >= minTargetPrice;           // above the floor → must still clear 2R
  };
  const targetCandidates =
    riskDistance !== null
      ? supportResistance.levelsAbove
          .filter(
            (z) => z.strength >= STRUCTURE_STRENGTH_MIN && passesProximityBand(z)
          )
          .sort((a, b) => a.priceLow - b.priceLow) // nearest qualifying first
      : [];
  const selectedExit = targetCandidates.length > 0 ? targetCandidates[0].priceLow : null;
  // realisticProfit: recomputed off the structure target. null when geometry is incomplete.
  const realisticProfit =
    selectedExit !== null
      ? ((selectedExit - entryClose) / entryClose) * 100
      : null;
  // ── LAYER 5 (Phase 6) — CANDLE-RELATIONSHIP EMITTER. A DESCRIBER, not a gate: walks the
  //    newest 20 candles of the in-hand oldest->newest window and emits a per-candle play-by-play
  //    array (solo shape + pair-vs-previous) onto the fired signal for the flywheel to pool on
  //    later. Does NOT judge, gate, veto, downgrade, or touch the score / geometry / any existing
  //    return field. Purely additive. Reuses orderedCandles (built above for FVG / S-R / L2 / L3);
  //    no re-fetch, no mutation. volAvg / rangeAvg are computed ONCE over the walked window.
  const L5_WINDOW = 20;
  const relWindow = orderedCandles.slice(-L5_WINDOW); // newest 20 (or fewer), oldest->newest
  const volAvg = relWindow.length
    ? relWindow.reduce((s, c) => s + c.volume, 0) / relWindow.length
    : 0;
  const rangeAvg = relWindow.length
    ? relWindow.reduce((s, c) => s + (c.high - c.low), 0) / relWindow.length
    : 0;
  const candleRelations = relWindow.map((c, i) => {
    // SOLO — this candle on its own.
    const body = Math.abs(c.close - c.open);
    const range = c.high - c.low;
    const bodyPct = range === 0 ? 0 : body / range;
    const direction = c.close > c.open ? "up" : c.close < c.open ? "down" : "flat";
    const upperWick = c.high - Math.max(c.open, c.close);
    const lowerWick = Math.min(c.open, c.close) - c.low;
    const bodyShape =
      bodyPct < 0.1 ? "doji" :
      bodyPct < 0.3 ? "small" :
      bodyPct > 0.9 ? "marubozu" : "normal";
    const wickUnit = body || range || 1;
    const wickShape =
      lowerWick >= 2 * wickUnit && upperWick < wickUnit ? "hammer" :
      upperWick >= 2 * wickUnit && lowerWick < wickUnit ? "shootingStar" : "balanced";
    const volLevel =
      c.volume < 0.7 * volAvg ? "low" :
      c.volume > 2.5 * volAvg ? "ultra" :
      c.volume > 1.5 * volAvg ? "high" : "normal";
    const spreadLevel =
      range < 0.7 * rangeAvg ? "narrow" :
      range > 1.5 * rangeAvg ? "wide" : "normal";
    // PAIR — this candle vs the PREVIOUS in the walked window; null for the first candle.
    const prev = i > 0 ? relWindow[i - 1] : null;
    const engulf = prev
      ? Math.min(c.open, c.close) <= Math.min(prev.open, prev.close) &&
        Math.max(c.open, c.close) >= Math.max(prev.open, prev.close)
      : null;
    const volumeVsPrev = prev
      ? (c.volume > prev.volume ? "rising" : c.volume < prev.volume ? "falling" : "equal")
      : null;
    const spreadVsPrev = prev
      ? (range > prev.high - prev.low ? "expanding" : range < prev.high - prev.low ? "contracting" : "equal")
      : null;
    const closeVsPrev = prev
      ? (c.close > prev.close ? "up" : c.close < prev.close ? "down" : "equal")
      : null;
    return {
      direction, bodyShape, wickShape, volLevel, spreadLevel,
      bodyPct: Math.round(bodyPct * 1000) / 1000,
      engulf, volumeVsPrev, spreadVsPrev, closeVsPrev,
    };
  });
  // ── REGIME CLASSIFIER v1 (Variant 2) — OBSERVE-ONLY derive + classify. Reuses orderedCandles
  //    (oldest→newest, lows from c.low) and values already computed above (macdHistogramHistory
  //    input, rsiValue, trendUp, volumeSurge). Does NOT touch confidenceScore or any gate; the
  //    result is attached as `regime` for the regime_states write path only.
  const structure = detectStructure(orderedCandles);
  const macdSlope =
    macdHistogramHistory.length >= 3
      ? macdHistogramHistory[macdHistogramHistory.length - 1] -
        macdHistogramHistory[macdHistogramHistory.length - 2]
      : 0;
  const rsiBand = rsiValue >= 55 ? 1 : rsiValue <= 45 ? -1 : 0;
  const regime = classifyRegime({ structure, macdSlope, rsiBand, trendUp, volumeSurge });
  return { confidenceScore, selectedExit, realisticProfit, confirmFromHigherTimeframes, stochRsiK, fvgs, supportResistance, shortBreakout, longBreakout, volumeSurge, trendUp, regimeAligned, structureBreak, retestHeld, absorptionDetected, effortDecayDetected, wickDefense, wickRejection, toppingWick, fireCandleOk, chopGateOk, candleRelations, regime, orderedCandles };
}

// ───────────────────────────────────────────────────────────────
// More Info indicator SERIES (Build 3, Sub-build 1) — DISPLAY-ONLY, additive.
// Expose per-candle indicator series for the More Info subpanels. These do NOT
// touch scoring: rsi / calculateIndicators / scoreTrade above are byte-identical.
// Each series assumes `closes` ascending (oldest→newest); `times` is the parallel
// ascending candle-time array. Series are NEWEST-ANCHORED and SHORTER than the
// candle set (warmup candles at the start get no entry). Render is a later sub-build.

// Rolling RSI matching calculateIndicators' convention (rsi over all closes up to i —
// the same value the score's rsiValue uses). Reuses the exported `rsi` helper.
export const rsiSeries = (closes, times, period = 14) => {
  const out = [];
  for (let i = period; i < closes.length; i++) {
    out.push({ time: times[i], value: rsi(closes.slice(0, i + 1), period) });
  }
  return out;
};

// NOTE: parallels macdCalc inside calculateIndicators (ema / macdLine / signalLn).
// Duplicated deliberately so scoreTrade / calculateIndicators stay byte-identical;
// unify in a post-score-gate dedup build. Keep in sync by hand until then.
export const macdSeries = (closes, times, short = 12, long = 26, signal = 9) => {
  const ema = (arr, period) => {
    if (!arr.length) return [];
    const k = 2 / (period + 1);
    return arr.reduce((acc, val, i) => {
      if (i === 0) return [val];
      acc.push(val * k + acc[i - 1] * (1 - k));
      return acc;
    }, []);
  };
  const emaShort = ema(closes, short);
  const emaLong = ema(closes, long);
  const macdLine = emaShort.map((v, i) => v - emaLong[i]);
  const start = Math.max(0, long - short);
  const signalLn = ema(macdLine.slice(start), signal);
  const out = [];
  for (let i = start; i < closes.length; i++) {
    const sig = signalLn[i - start];
    out.push({ time: times[i], macd: macdLine[i], signal: sig, histogram: macdLine[i] - sig });
  }
  return out;
};

// StochRSI series intentionally omitted: engine StochRSI is known-incorrect (audit
// 2026-06-13); add a CORRECT series here as part of the score-gate StochRSI fix.

// Bollinger Bands — standard SMA(period) middle band + upper/lower at mult * population
// stdev. DISPLAY-ONLY, additive, same newest-anchored/shorter-than-candles convention as
// rsiSeries/macdSeries above (first `period - 1` candles get no entry — no warmup value).
export const bollingerSeries = (closes, times, period = 20, mult = 2) => {
  const out = [];
  for (let i = period - 1; i < closes.length; i++) {
    const slice = closes.slice(i - period + 1, i + 1);
    const mean = slice.reduce((a, b) => a + b, 0) / period;
    const variance = slice.reduce((a, b) => a + (b - mean) * (b - mean), 0) / period;
    const sd = Math.sqrt(variance);
    out.push({ time: times[i], upper: mean + mult * sd, middle: mean, lower: mean - mult * sd });
  }
  return out;
};

// One call for the More Info effect: extract closes + times ONCE, fan out to the series.
// Guard empty input. Returns the newest-anchored series; draws nothing.
export const computeMoreInfoIndicators = (formatted) => {
  if (!formatted || formatted.length === 0) return { rsi: [], macd: [], bollinger: [] };
  const closes = formatted.map((c) => c.close);
  const times = formatted.map((c) => c.time);
  return { rsi: rsiSeries(closes, times), macd: macdSeries(closes, times), bollinger: bollingerSeries(closes, times) };
};
